import {
  IThumbnailVerticalHoverBlockDemo,
  IThumbnailVerticalHoverBlockGame,
  IThumbnailVerticalHoverBlockLikes,
} from '@spinwise/react-component-library/ThumbnailVerticalHoverBlock';
import {
  IApplicationState,
  IGame,
  OVERLAY_GAME_POPUP_SHOW,
  OVERLAY_LOGIN_SHOW,
  OVERLAY_PAYNPLAY_LOGIN_SHOW,
  OVERLAY_PROMOTION_SHOW,
  addGameToFavorite,
  removeGameFromFavorite,
  OVERLAY_ALL_CLOSE,
  IReducerWarningOverlay,
  OVERLAY_WARNING_NOTIFICATION_SET_CONTENT,
  OVERLAY_WARNING_NOTIFICATION_SHOW,
} from '@spinwise/spinwise-core';
import { useEffect, useState } from 'react';
import ThumbnailElement from '../CarouselBlock/CarouselElements/Thumbnail/ThumbnailElement';
import { useDispatch, useSelector } from 'react-redux';
import router from 'next/router';
import { useRegType } from 'Hooks/useRegType';
import { t } from 'i18next';
import { ECurrency, RegTypes } from 'Types/globals';
import { EWarningOverlayTypes } from '../Overlay/elements/WarningOverlayManager/WarningOverlayManagerComponent';
import { BrandIcons, ShapeColors, StatusIcons } from '../BrandIcon/BrandIcon';
import useGameLockCheck from 'Hooks/useGameLockCheck';
import { BasicSquareThumbnail } from '@spinwise/react-component-library/BasicSquareThumbnail';
import { TournamentVerticalThumbnail } from '@spinwise/react-component-library/TournamentVerticalThumbnail';
import { LiveVerticalThumbnail } from '@spinwise/react-component-library/LiveVerticalThumbnail';
import { SlotVerticalThumbnail } from '@spinwise/react-component-library/SlotVerticalThumbnail';
import useShowAuthButtons from 'Hooks/useShowAuthButtons';

export enum GamesCategories {
  Tournament = 'Tournament',
  LiveCasino = 'Live Casino',
  New = 'New',
  FeatureBuy = 'Feature buy',
}

const GameThumbnailManager = (props: {
  game: IGame;
  integrationKeys: any;
  size: number;
  isSquare?: boolean;
  isTextContainer?: boolean;
}) => {
  const integrationKeys = useSelector((state: IApplicationState) => state.globalReducer?.integrationKeys);
  const authSuccess = useSelector((state: IApplicationState) => state.authReducer?.auth?.success);
  const isMonetaryActivityEnabled = useSelector(
    (store: IApplicationState) => store.accountReducer?.info?.isMonetaryActivityEnabled,
  );
  const activeBonus = useSelector((store: IApplicationState) => store.accountReducer?.info?.activeBonus);
  const isMaxbetPopupShown = useSelector((store: IApplicationState) => store.accountReducer?.info?.isMaxbetPopupShown);

  const [currentSize, setCurrentSize] = useState(props.size);
  const [isFavorite, setIsFavorite] = useState(false);

  const dispatch = useDispatch();
  const isRegType = useRegType();
  const isClassic = isRegType([RegTypes.EMAIL, RegTypes.DEPOSIT]);

  const showLoginButton = useShowAuthButtons();

  const isGameLockedByBonus = useGameLockCheck()(props?.game.allowBonus);

  const isGameLocked = authSuccess && isMonetaryActivityEnabled === false ? true : isGameLockedByBonus;

  useEffect(() => {
    setCurrentSize(props.size);
  }, [props.size]);

  useEffect(() => {
    setIsFavorite(props?.game?.isFavorite);
  }, [props?.game?.isFavorite]);

  const getGameCategory = (category: string) => {
    const gameCategory = props?.game?.category
      .map(item => item.toLocaleLowerCase())
      .includes(category.toLocaleLowerCase());
    return gameCategory;
  };

  const handleFavoriteClick = () => {
    isFavorite ? removeGameFromFavorite(props?.game.eid)(dispatch) : addGameToFavorite(props?.game.eid)(dispatch);
    setIsFavorite(!isFavorite);
  };

  const toggleGamePopup = (open: boolean, maxBetsLimited?: boolean) => {
    dispatch({
      type: OVERLAY_PROMOTION_SHOW,
      content: { show: false },
    });

    dispatch({
      type: OVERLAY_GAME_POPUP_SHOW,
      data: {
        show: open,
        data: open
          ? {
              eid: props?.game.eid,
              name: props?.game.name,
              provider: props?.game.provider.name,
              imgUrl: props?.game.thumbnail,
              urlKey: props?.game?.urlKey,
              game: props?.game,
              integrationKeys,
              isGameLockedByBonus,
              isMonetaryActivityEnabled: isMonetaryActivityEnabled,
              isLiveGame: !props?.game.hasDemo,
              isFavorite,
              loggedIn: authSuccess,
              maxBetsLimited: maxBetsLimited,
            }
          : {},
      },
    });
  };

  const openSignInPopup = () => {
    toggleGamePopup(false);

    if (!showLoginButton) return;
    
    dispatch({
      type: isClassic ? OVERLAY_LOGIN_SHOW : OVERLAY_PAYNPLAY_LOGIN_SHOW,
      data: true,
    });
  };

  const onPlayClick = () => {
    if (authSuccess) {
      isGameLockedByBonus ? toggleGameLockedPopUp() : isGameLocked ? toggleGamePopup(true) : handleMaxBetLimitedClick();
    } else {
      openSignInPopup();
    }
  };

  const handleMaxBetLimitedClick = () => {
    const isWager = activeBonus?.coefficient;
    if (
      activeBonus?.data?.max_bet_forced == 'yes' &&
      !isMaxbetPopupShown &&
      !isWager &&
      !localStorage?.getItem('maxBetsLimitedPopUp')
    ) {
      toggleGamePopup(true, true);
    } else {
      router.push(`/play/${props?.game?.urlKey}`);
    }
  };

  const onMobilePlayClick = () => {
    const isWager = activeBonus?.coefficient;
    if (isGameLockedByBonus) {
      toggleGameLockedPopUp();
    } else {
      if (
        activeBonus?.data?.max_bet_forced == 'yes' &&
        !isMaxbetPopupShown &&
        !isWager &&
        !localStorage?.getItem('maxBetsLimitedPopUp')
      ) {
        toggleGamePopup(true, true);
      } else {
        toggleGamePopup(true);
      }
    }
  };

  const setRestrictedGameWarningContent = () => {
    let data: IReducerWarningOverlay = {
      headerTitle: t('restricted.game.header.title'),
      type: EWarningOverlayTypes.RestrictedGame,
      btnAcceptText: null,
      btnDeniedText: null,
      title: t('restricted.game.warning.title'),
      subtitle: t('restricted.game.warning.subtitle'),
      icon: BrandIcons.NewGame,
      shapeColor: ShapeColors.Warning,
      status: StatusIcons.Faild,
    };

    dispatch({
      type: OVERLAY_WARNING_NOTIFICATION_SET_CONTENT,
      data: data,
    });
  };

  const toggleGameLockedPopUp = () => {
    setRestrictedGameWarningContent();

    dispatch({
      type: OVERLAY_WARNING_NOTIFICATION_SHOW,
      data: true,
    });
  };

  const getBetText = (type: 'min' | 'max', bet: { currency; value; symbol? }): string => {
    let result = parseLiveGameBet(bet);
    if (type == 'max') return result ?? `€5 000`;
    else return result ?? `€5`;
  };
  const parseLiveGameBet = (bet: { value: string; currency: any; symbol?: string } | null): string | undefined => {
    if (bet == null || bet == undefined) return undefined;

    let symbol = bet?.symbol ?? ECurrency[bet.symbol];

    if (!symbol || !bet?.value) return undefined;

    return `${symbol}${bet?.value}`;
  };
  const getTypeThumbnail = (item: IGame, integrationKey: any) => {
    const imgHost = '/images/thumbs';
    const demoUrl = `/demo/${item?.urlKey}`;
    const playUrl = `/play/${item?.urlKey}`;

    const closeAllOverlay = () => {
      dispatch({
        type: OVERLAY_ALL_CLOSE,
        data: true,
      });
    };

    const demoSettings: IThumbnailVerticalHoverBlockDemo = {
      demoHref: demoUrl,
      demoTitle: 'Demo',
      onClickDemo: () => {
        closeAllOverlay();
        router.push(demoUrl);
      },
    };
    const gameSettings: IThumbnailVerticalHoverBlockGame = {
      gameHref: playUrl,
      onClickPlay: () => {
        closeAllOverlay();

        if (!showLoginButton) return;
        
        onPlayClick();
      },
    };
    const favoriteSettings: IThumbnailVerticalHoverBlockLikes = {
      isFavorite: isFavorite,
      onClickFavorite: () => {
        authSuccess ? handleFavoriteClick() : openSignInPopup();
      },
    };
    if (typeof integrationKeys?.keys === 'boolean') {
      return (
        <ThumbnailElement
          widthItem={currentSize}
          key={`${item?.provider?.keyword}-${item?.name}`}
          name={item?.name}
          imgUrl={`${item.thumbnail}`}
          urlKey={item?.urlKey}
          isFavorite={item.isFavorite}
          eid={item.eid}
          category={item.category}
          minBet={parseLiveGameBet(item.minBet)}
          maxBet={parseLiveGameBet(item.maxBet)}
          provider={item?.provider?.name}
          allowBonus={item.allowBonus}
          hasDemo={item.hasDemo}
        />
      );
    }
    if (integrationKey == undefined) return null;
    if (props.size === undefined) return null;
    if (getGameCategory(GamesCategories.Tournament)) {
      if (props.isSquare) {
        return (
          <BasicSquareThumbnail
            maxWidth={currentSize}
            prettyGameName={item?.name}
            prettyProviderName={item?.provider?.name}
            urlSettings={{
              format: integrationKey.format,
              rawGameName: integrationKey.thumbnail,
              gradient: integrationKey.gradient,
              imgHost: imgHost,
              providerId: integrationKey.providerId,
              templateId: integrationKey.templateId,
              version: integrationKey.version,
            }}
            lockedSettings={{
              isLocked: isGameLocked,
            }}
            tournamentOptions={{
              tournamentText: t('game.tournament.label'),
            }}
            isTextContainer={props?.isTextContainer}
          />
        );
      } else {
        return (
          <TournamentVerticalThumbnail
            prettyGameName={item?.name}
            prettyProviderName={item?.provider?.name}
            urlSettings={{
              format: integrationKey.format,
              rawGameName: integrationKey.thumbnail,
              gradient: integrationKey.gradient,
              imgHost: imgHost,
              providerId: integrationKey.providerId,
              templateId: integrationKey.templateId,
              version: integrationKey.version,
            }}
            tournamentOptions={{
              providerImgUrl: `https://cdn-cms.spinwise.com/providers_${integrationKey.providerId}_mono`,
              tournamentText: t('game.tournament.label'),
            }}
            lockedSettings={{
              isLocked: isGameLocked,
              title: t('game.locked.label'),
            }}
            onClickMobileThumbnail={() => onMobilePlayClick()}
            maxWidth={currentSize}
            hoverElementDemo={item.hasDemo == 1 ? demoSettings : undefined}
            hoverElementFavorite={favoriteSettings}
            hoverElementGame={gameSettings}
          />
        );
      }
    }
    if (getGameCategory(GamesCategories.LiveCasino)) {
      if (props.isSquare) {
        return (
          <BasicSquareThumbnail
            urlSettings={{
              format: integrationKey.format,
              rawGameName: integrationKey.thumbnail,
              gradient: integrationKey.gradient,
              imgHost: imgHost,
              providerId: integrationKey.providerId,
              templateId: integrationKey.templateId,
              version: integrationKey.version,
            }}
            maxWidth={currentSize}
            liveOptions={{
              isOnline: true,
              betFrom: getBetText('min', item.minBet),
              betTo: getBetText('max', item.maxBet),
              offlineText: t('game.offline.label'),
              onlineText: t('game.online.label'),
            }}
            prettyGameName={item?.name}
            prettyProviderName={item?.provider?.name}
            lockedSettings={{
              isLocked: isGameLocked,
            }}
            isTextContainer={props?.isTextContainer}
          />
        );
      } else {
        return (
          <LiveVerticalThumbnail
            urlSettings={{
              format: integrationKey.format,
              rawGameName: integrationKey.thumbnail,
              gradient: integrationKey.gradient,
              imgHost: imgHost,
              providerId: integrationKey.providerId,
              templateId: integrationKey.templateId,
              version: integrationKey.version,
            }}
            lockedSettings={{
              isLocked: isGameLocked,
              title: t('game.locked.label'),
            }}
            onClickMobileThumbnail={() => onMobilePlayClick()}
            maxWidth={currentSize}
            prettyGameName={item?.name}
            prettyProviderName={item?.provider?.name}
            liveOptions={{
              isOnline: true,
              betFrom: getBetText('min', item.minBet),
              betTo: getBetText('max', item.maxBet),
              offlineText: t('game.offline.label'),
              onlineText: t('game.online.label'),
            }}
            hoverElementDemo={item.hasDemo == 1 ? demoSettings : undefined}
            hoverElementFavorite={favoriteSettings}
            hoverElementGame={gameSettings}
          />
        );
      }
    }

    if (props.isSquare) {
      return (
        <BasicSquareThumbnail
          prettyGameName={item?.name}
          prettyProviderName={item?.provider?.name}
          maxWidth={currentSize}
          urlSettings={{
            format: integrationKey.format,
            rawGameName: integrationKey.thumbnail,
            gradient: integrationKey.gradient,
            imgHost: imgHost,
            providerId: integrationKey.providerId,
            templateId: integrationKey.templateId,
            version: integrationKey.version,
          }}
          lockedSettings={{
            isLocked: isGameLocked,
          }}
          isTextContainer={props?.isTextContainer}
        />
      );
    } else {
      return (
        <SlotVerticalThumbnail
          prettyGameName={item?.name}
          prettyProviderName={item?.provider?.name}
          urlSettings={{
            format: integrationKey.format,
            rawGameName: integrationKey.thumbnail,
            gradient: integrationKey.gradient,
            imgHost: imgHost,
            providerId: integrationKey.providerId,
            templateId: integrationKey.templateId,
            version: integrationKey.version,
          }}
          lockedSettings={{
            isLocked: isGameLocked,
            title: t('game.locked.label'),
          }}
          onClickMobileThumbnail={() => onMobilePlayClick()}
          maxWidth={currentSize}
          hoverElementDemo={item.hasDemo == 1 ? demoSettings : undefined}
          hoverElementFavorite={favoriteSettings}
          hoverElementGame={gameSettings}
        />
      );
    }
  };
  return <>{getTypeThumbnail(props?.game, props.integrationKeys)}</>;
};
export default GameThumbnailManager;
